import type { AnyRouter } from '@tanstack/react-router';

export type CurrentRouteParams = Record<string, string | undefined>;

let activeRouter: AnyRouter | undefined;

/**
 * Registered once at startup by the app root.
 *
 * The router is handed in rather than imported so this module stays outside the
 * router's import graph: it is consumed by thunks and selectors, and importing
 * router.ts here would close a ducks -> router -> routes -> pages -> ducks
 * cycle that breaks type inference across the package.
 */
export function setActiveRouter(router: AnyRouter): void {
  activeRouter = router;
}

/**
 * Drops the registered router. Tests register a memory router per render, so
 * without this a later test in the same file reads the previous test's router
 * through getCurrentRouteParams, which is order dependent and hard to spot.
 */
export function clearActiveRouter(): void {
  activeRouter = undefined;
}

/**
 * Route params of the currently committed leaf match, for callers that cannot
 * use hooks (thunks, action creators, context-menu definitions) — anything
 * invoked imperatively, which reads the value once at call time.
 *
 * NOT for render paths. This is an imperative read of a mutable router, so
 * neither React nor react-redux can know the value changed: a component using
 * useAppSelector, or a mapStateToProps calling this, keeps rendering the
 * previous route's params after a navigation. Components use useParams;
 * connect()-based components read state.routeParams until they are converted
 * to function components (FE-7111).
 */
export function getCurrentRouteParams(): CurrentRouteParams {
  const matches = activeRouter?.state.matches;
  return matches?.[matches.length - 1]?.params ?? {};
}

/**
 * v5 route pattern of the currently committed leaf match.
 *
 * Pair this with getCurrentRouteParams rather than reading
 * state.routeParams.route: the Redux mirror is published synchronously on the
 * history event, so combining it with a match-keyed id yields a route and an
 * id belonging to two different pages during a navigation.
 */
export function getCurrentRoutePattern(): string {
  const matches = activeRouter?.state.matches;
  return matches?.[matches.length - 1]?.staticData?.v5Pattern ?? '';
}
